// Copyright © 2017-2020  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC.

<template>
  <v-container fluid grid-list-md>
    <v-row wrap>
      <v-col cols="12" justify-center>
        <v-card>
          <v-progress-linear :indeterminate="loading" style="margin-top: 0px;"></v-progress-linear>
          <v-card-text>
            <v-row wrap>
              <v-col cols="12" md="4">
                <date-time-view
                v-model="dateStart"
                label="Start Date"
                ></date-time-view>
              </v-col>
              <v-col cols="12" md="4">
                <date-time-view
                v-model="dateEnd"
                label="End Date"
                ></date-time-view>
              </v-col>
              <v-col cols="12" md="4">
                <v-btn dark color="green" class="mx-1" :href="downloadUrl" download>Download XLSX</v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <v-btn dark color="blue darken-1" class="mx-1" @click="resetDate()">Today</v-btn>
                <v-btn dark color="blue darken-2" class="mx-1" @click="resetWeek()">Last 7 Days</v-btn>
                <v-btn dark color="blue darken-3" class="mx-1" @click="resetMonth()">Last 30 Days</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row wrap>
      <v-col cols="12">
        <item-list
        :endpoint="endpoint + 'exceptions'"
        :filter="localFilters"
        :headers="headers"
        :loading="loading"
        editRoute="exceptionDetail"
        id="_id"
        name="Item"
        permissions="exceptions"
        ></item-list>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import {ENDPOINT, EXCEPTIONTYPES} from '@/shared/constants'
import ApiRequests from '@/api/ApiRequests'
import DateTimeView from '@/components/DateTimeView'
import ItemList from '@sdgsystems/vue-common/components/ItemList.vue'
import {LookupMixin, TimeMixin, SettingsMixin} from '@/shared/mixins'

export default {
  components: {
    DateTimeView,
    ItemList
  },

  mixins: [LookupMixin, TimeMixin, SettingsMixin],

  data () {
    return {
      headers: [],
      dateStart: moment().startOf('day').format(),
      dateEnd: moment().add(1, 'days').startOf('day').format(),
      endpoint: ENDPOINT,
      items: [],
      loading: false,
      things: [],
      zones: []
    }
  },

  created () {
    if (Object.keys(this.siteSettings).length > 0) this.createHeader()
    Promise.all([
      ApiRequests.getThings().then(r => {
        this.things = r.data
        return r
      }),
      ApiRequests.getZones(this.selectedSite._id).then(r => {
        this.zones = r.data
        return r
      })
    ])
  },

  computed: {
    ...mapGetters([
      'selectedSite',
      'siteSettings'
    ]),

    localFilters: function () {
      var f = [] // Object.assign([], this.filters) don't want to filter by site for now
      if (this.dateStart) {
        f.push({'name': 'after', 'value': this.dateStart})
      }
      if (this.dateEnd) {
        f.push({'name': 'before', 'value': this.dateEnd})
      }
      f.push({'name': 'site', 'value': this.selectedSite._id})
      f.push({'name': 'sort', 'value': '-time'})
      return f
    },

    downloadUrl: function () {
      var params = {
        site: this.selectedSite._id
      }

      if (this.dateStart) {
        params.after = this.dateStart
      }
      if (this.dateEnd) {
        params.before = this.dateEnd
      }
      var url = ENDPOINT + 'exceptions/export?' + new URLSearchParams(params).toString()
      return url
    }
  },

  methods: {
    createHeader: function () {
      this.headers = [
        {
          text: 'Time',
          value: 'time',
          function: this.formatDateTime,
          sortable: true
        },
        {
          text: this.getItemNameLabel(this.siteSettings.itemNameLabel),
          value: 'thing',
          function: this.lookupThingName,
          sortable: false
        },
        {
          text: 'Type',
          value: 'type',
          function: this.lookupType,
          sortable: true
        },
        {
          text: 'Zone',
          value: 'zone',
          function: this.lookupZoneName,
          sortable: false
        },
        {
          text: 'Reason',
          value: 'reason',
          function: this.lookupReason,
          sortable: false
        }
      ]
    },

    formatDateTime: function (item) {
      return this.formatTime(item.time)
    },

    formatDuration: function (item) {
      var duration = moment.duration(item.timeDuration / 1000000)
      return Math.floor(duration.as('hours')) + ':' + this.pad(duration.minutes()) + ':' + this.pad(duration.seconds())
    },

    lookupThingName: function (item) {
      return this.getThingName(item.thing)
    },

    lookupZoneName: function (item) {
      return this.getZoneName(item.zone)
    },

    lookupReason: function (item) {
      if (item.type === EXCEPTIONTYPES.zoneTimeExceptions) {
        return '> ' + moment.duration(this.getZone(item.zone).duration / 1000000).asMinutes() + ' minute(s)'
      } else if (item.type === 'processException') {
        return 'Expected in Zone 6'
      } else if (item.type === EXCEPTIONTYPES.invThingsMissing) {
        return `Shortage of ${item.multipleThings.length}`
      } else if (item.type === EXCEPTIONTYPES.invThingsUnexpected) {
        return `Overage of ${item.multipleThings.length}`
      }
      return 'N/A'
    },

    lookupType: function (item) {
      console.log(item)
      switch (item.type) {
        case EXCEPTIONTYPES.zoneTimeExceptions:
          return 'Zone Time Exception'
        case EXCEPTIONTYPES.invThingsMissing:
          return 'Inventory - shortage'
        case EXCEPTIONTYPES.invThingsUnexpected:
          return 'Inventory - overage'
        default:
          return 'Unknown'
      }
    },

    resetDate: function () {
      this.dateStart = moment().startOf('day').format()
      this.dateEnd = moment().add(1, 'days').startOf('day').format()
    },

    resetWeek: function () {
      this.dateStart = moment().startOf('day').subtract(6, 'days').format()
      this.dateEnd = moment().add(1, 'days').startOf('day').format()
    },

    resetMonth: function () {
      this.dateStart = moment().startOf('day').subtract(29, 'days').format()
      this.dateEnd = moment().add(1, 'days').startOf('day').format()
    }
  },

  watch: {
    siteSettings: function () {
      this.createHeader()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/ReportExceptions.vue